import { SisterSearcherRequestStatus, SisterSearcherVesselOption } from "../models/sister-searcher.models";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

interface SisterSearcherVesselSelectorProps {
  buttonLabel?: string;
  error: string;
  onQueryChange: (query: string) => void;
  onSearch: () => void;
  onSelect: (imo: string) => void;
  options: SisterSearcherVesselOption[];
  query: string;
  searchStatus: SisterSearcherRequestStatus;
  selectedImo: string;
  status: SisterSearcherRequestStatus;
}

// Loads a small, selectable main result table so vessel choice remains usable for both name and IMO searches.
// The explicit search action runs the similarity criteria against the chosen reference vessel.
const SisterSearcherVesselSelector = ({
  buttonLabel = "Find sister vessels",
  error,
  onQueryChange,
  onSearch,
  onSelect,
  options,
  query,
  searchStatus,
  selectedImo,
  status,
}: SisterSearcherVesselSelectorProps) => (
  <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="flex flex-wrap items-end gap-3">
      <label className="min-w-[260px] flex-1">
        <span className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-300">Vessel name or IMO</span>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search the vessel list"
            className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 outline-none focus:border-green focus:ring-1 focus:ring-green dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            data-test="sister-searcher-vessel-query-input"
          />
        </div>
      </label>
      <button
        type="button"
        onClick={onSearch}
        disabled={!selectedImo || searchStatus === "loading"}
        className="h-10 rounded-lg bg-green px-5 text-xs font-semibold text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
        data-test="sister-searcher-search-button"
      >
        {searchStatus === "loading" ? "Searching..." : buttonLabel}
      </button>
    </div>

    <div className="mt-3 max-h-48 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-left text-xs" data-test="sister-searcher-main-results-table">
        <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
          <tr>
            <th className="w-10 px-3 py-2"><span className="sr-only">Select</span></th>
            <th className="px-3 py-2 font-semibold">IMO</th>
            <th className="px-3 py-2 font-semibold">Vessel Name</th>
          </tr>
        </thead>
        <tbody>
          {options.map((option) => (
            <tr
              key={option.imo}
              onClick={() => onSelect(option.imo)}
              className={twMerge(
                "cursor-pointer border-t border-gray-100 text-gray-700 hover:bg-primary-10 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-teal-950/40",
                selectedImo === option.imo && "bg-primary-10 dark:bg-teal-950/40",
              )}
              data-test={`sister-searcher-vessel-option-${option.imo}`}
            >
              <td className="px-3 py-2">
                <input
                  type="radio"
                  name="sister-searcher-selected-vessel"
                  checked={selectedImo === option.imo}
                  onChange={() => onSelect(option.imo)}
                  aria-label={`Select ${option.vesselName}`}
                  className="h-4 w-4 text-green focus:ring-green"
                />
              </td>
              <td className="px-3 py-2 font-mono text-green">{option.imo}</td>
              <td className="px-3 py-2 font-semibold text-gray-900 dark:text-gray-100">{option.vesselName}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {status === "loading" && (
        <p className="px-4 py-6 text-center text-xs text-gray-500" data-test="sister-searcher-vessels-loading">
          Loading vessels...
        </p>
      )}
      {status === "error" && (
        <p className="px-4 py-6 text-center text-xs text-red-700 dark:text-red-300" data-test="sister-searcher-vessels-error">
          {error}
        </p>
      )}
      {status === "success" && options.length === 0 && (
        <p className="px-4 py-6 text-center text-xs text-gray-500" data-test="sister-searcher-vessels-empty">
          No vessels match this name or IMO.
        </p>
      )}
    </div>
  </section>
);

export default SisterSearcherVesselSelector;
